/**
 * Propuesta de contrapartida para movimientos Norma 43.
 * Orden: préstamos (solo loan_schedule, nunca leasings) → reglas fijas →
 * clientes (abonos) → proveedores (cargos).
 */
import type { N43Movement } from "./norma43";

export interface LoanInstallment {
  contractNumber: string;
  bankName: string;
  installmentNumber: number;
  dueDate: string; // YYYY-MM-DD
  total: number;
  principal: number;
  interest: number;
  insurance: number;
}
export interface MasterEntry { account: string; name: string; taxId: string }
export interface MatchContext { loans: LoanInstallment[]; customers: MasterEntry[]; suppliers: MasterEntry[] }

export type MatchSource = "prestamo" | "regla" | "cliente" | "proveedor" | "sin_asignar";
export interface SplitLine { account: string; label: string; amount: number }
export interface MatchSuggestion {
  source: MatchSource;
  account: string;
  label: string;
  split?: SplitLine[];
}

export const LOAN_ACCOUNTS = { principal: "52000000", interest: "66230000", insurance: "62500000" };

const norm = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
const cleanTax = (s: string) => (s || "").toUpperCase().replace(/^ES/, "").replace(/[^A-Z0-9]/g, "");
const daysBetween = (a: string, b: string) => Math.abs((Date.parse(a) - Date.parse(b)) / 86400000);
const r2 = (n: number) => Math.round(n * 100) / 100;

const TAX_CORE = "([A-HJNP-SUVW]\\d{7}[0-9A-J]|\\d{8}[A-Z]|[XYZ]\\d{7}[A-Z])";
const TAX_RE = new RegExp(`\\b(?:ES)?${TAX_CORE}\\b`, "g");
// Identificador de acreedor SEPA: ES + 2 dígitos de control + 3 caracteres de sufijo + NIF
const SEPA_CREDITOR_RE = new RegExp(`\\bES\\d{2}[A-Z0-9]{3}${TAX_CORE}\\b`, "g");

export function movementText(m: N43Movement): string {
  // Los adeudos SEPA anteponen el esquema (CORE/B2B) pegado al nombre del acreedor.
  const concepts = m.concepts.map((c) => c.replace(/^\s*(CORE|B2B)(?=[A-Za-zÁÉÍÓÚÑ])/i, ""));
  return [m.reference1, m.reference2, m.documentNumber, ...concepts].join(" ");
}
export function extractTaxIds(text: string): string[] {
  const out = new Set<string>();
  const up = text.toUpperCase();
  for (const x of up.matchAll(SEPA_CREDITOR_RE)) out.add(x[1]);
  for (const x of up.matchAll(TAX_RE)) out.add(x[1]);
  return [...out];
}

function matchLoan(m: N43Movement, loans: LoanInstallment[], used: Set<string>): LoanInstallment | null {
  if (m.amount >= 0) return null;
  const amt = Math.abs(m.amount);
  const digits = norm(movementText(m)).replace(/ /g, "");
  let best: { l: LoanInstallment; score: number } | null = null;
  for (const l of loans) {
    const key = `${l.contractNumber}#${l.installmentNumber}`;
    if (used.has(key)) continue;
    if (Math.abs(l.total - amt) > 0.02) continue;
    const ctr = norm(l.contractNumber).replace(/ /g, "");
    const mentions = ctr.length >= 4 && digits.includes(ctr.slice(-6));
    const d = daysBetween(l.dueDate, m.operationDate);
    if (!mentions && d > 7) continue;
    if (mentions && d > 45) continue;
    const score = (mentions ? 0 : 100) + d;
    if (!best || score < best.score) best = { l, score };
  }
  if (best) used.add(`${best.l.contractNumber}#${best.l.installmentNumber}`);
  return best?.l ?? null;
}

function fixedRule(m: N43Movement, text: string): MatchSuggestion | null {
  const t = norm(text);
  if (/\bQ28\d{5}/.test(text.toUpperCase()) && /(TGSS|SEGURIDAD SOCIAL|SEG SOCIAL|SEGUROS SOCIALES)/.test(t))
    return { source: "regla", account: "47600000", label: "Seguridad Social" };
  if (/(TGSS|TESORERIA GENERAL|SEGURIDAD SOCIAL|SEGUROS SOCIALES)/.test(t))
    return { source: "regla", account: "47600000", label: "Seguridad Social" };
  if (/(AEAT|AGENCIA TRIBUTARIA|AGENCIA ESTATAL)/.test(t))
    return { source: "regla", account: "47500000", label: "Hacienda Pública" };
  if (m.commonConcept === "17" || (m.amount < 0 && /\b(COMISION|COMISIONES|MANTENIMIENTO|GASTOS BANCARIOS)\b/.test(t)))
    return { source: "regla", account: "62600000", label: "Servicios bancarios" };
  if (m.commonConcept === "12" && m.amount > 0)
    return { source: "regla", account: "57200099", label: "Liquidación TPV (cuenta puente)" };
  return null;
}

function matchMaster(text: string, list: MasterEntry[]): MasterEntry | null {
  const ids = extractTaxIds(text);
  if (ids.length) {
    const hit = list.find((e) => e.taxId && ids.includes(cleanTax(e.taxId)));
    if (hit) return hit;
  }
  const t = ` ${norm(text)} `;
  let best: MasterEntry | null = null;
  for (const e of list) {
    const n = norm(e.name).replace(/\b(S L|S A|SL|SA|SLU|S L U|SC|CB|SOCIEDAD LIMITADA)\b/g, "").trim();
    if (n.length < 5) continue;
    if (t.includes(` ${n} `) && (!best || n.length > norm(best.name).length)) best = e;
  }
  return best;
}

export function suggestForMovements(movements: N43Movement[], ctx: MatchContext): MatchSuggestion[] {
  const used = new Set<string>();
  return movements.map((m) => {
    const text = movementText(m);
    const loan = matchLoan(m, ctx.loans, used);
    if (loan) {
      const split: SplitLine[] = [
        { account: LOAN_ACCOUNTS.principal, label: "Amortización", amount: r2(loan.principal) },
        { account: LOAN_ACCOUNTS.interest, label: "Intereses", amount: r2(loan.interest) },
      ];
      if (loan.insurance) split.push({ account: LOAN_ACCOUNTS.insurance, label: "Seguro", amount: r2(loan.insurance) });
      return {
        source: "prestamo",
        account: LOAN_ACCOUNTS.principal,
        label: `Préstamo ${loan.bankName ? loan.bankName + " " : ""}ctto. ${loan.contractNumber} · cuota ${loan.installmentNumber}`,
        split,
      };
    }
    const rule = fixedRule(m, text);
    if (rule) return rule;
    if (m.amount > 0) {
      const c = matchMaster(text, ctx.customers);
      if (c) return { source: "cliente", account: c.account, label: c.name };
    } else {
      const s = matchMaster(text, ctx.suppliers);
      if (s) return { source: "proveedor", account: s.account, label: s.name };
    }
    return { source: "sin_asignar", account: "", label: "" };
  });
}
